// Perfiles de acceso del backoffice: que seccion ve cada cuenta y hasta donde.
//
// Un perfil (fila de `admin_roles`) es un mapa seccion → nivel. Los niveles no
// son los mismos en todas las secciones: cada una declara su propia cadena en
// shared/access/catalog.json, ordenada de menor a mayor y con `hidden` siempre
// primero. Las secciones sin lista propia usan la de siempre:
//
//   hidden  la seccion no existe para esa cuenta: ni en el menu, ni por URL,
//           ni en el manual, ni en la API.
//   read    ve la pantalla y sus datos; los botones que cambian algo no
//           aparecen y el backend los rechaza.
//   write   ve y opera (nivel MAXIMO de las secciones de tres niveles).
//
// Las secciones con mas escalones declaran su lista: `coupons` agrega `create`
// (emitir) y `revoke` (anular) por encima de `read`. Un perfil solo puede
// guardar niveles de la lista de ESA seccion: `write` sobre `coupons` no es "un
// poco mas", es un valor que no existe y cae a `hidden` (normalizeAccess).
//
// El catalogo vive en shared/access/catalog.json porque lo leen tres cosas que
// no comparten codigo: este modulo (backend y portal), la CloudFront Function
// del manual (via Terraform) y el menu del manual (Astro). Aca se le agregan
// los tipos y las etiquetas; el orden de las secciones es el del catalogo y NO
// se reordena: el pase de lectura del manual codifica un caracter por seccion
// en ese orden.
//
// Este modulo no importa nada de Node ni de React a proposito: lo compilan
// esbuild (Lambdas), Vite (admin-portal) y vitest.

import catalog from '../../shared/access/catalog.json' with { type: 'json' };

export const ACCESS_SECTIONS = catalog.sections as readonly AccessSection[];

export type AccessSection =
  | 'dashboard'
  | 'users'
  | 'partners'
  | 'catalog'
  | 'reports'
  | 'invoices'
  | 'jobs'
  | 'audit'
  | 'disposable'
  | 'team'
  | 'coupons';

// El conjunto GLOBAL de niveles posibles. El nivel valido de una seccion es el
// de su propia cadena (levelsFor): un nivel global que esa seccion no declara
// no se puede guardar en ella.
export const ACCESS_LEVELS = ['hidden', 'read', 'write', 'create', 'revoke'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export type AccessMap = Record<AccessSection, AccessLevel>;

/** La cadena de niveles que declara cada seccion en el catalogo. */
const CATALOG_LEVELS = catalog.levels as Readonly<Record<string, readonly AccessLevel[]>>;

/** Niveles de las secciones que no declaran una lista propia. */
const DEFAULT_LEVELS: readonly AccessLevel[] = ['hidden', 'read', 'write'];

/**
 * Niveles de una seccion, de menor a mayor, con `hidden` primero. El indice de
 * un nivel en esta lista es lo que codifica el pase del manual, asi que el
 * orden no se puede tocar sin invalidar los pases ya emitidos.
 */
export function levelsFor(section: AccessSection): readonly AccessLevel[] {
  return CATALOG_LEVELS[section] ?? DEFAULT_LEVELS;
}

/** Nivel mas alto de una seccion: `write` donde hay tres, `revoke` donde hay cuatro. */
export function maxLevelFor(section: AccessSection): AccessLevel {
  const levels = levelsFor(section);
  return levels[levels.length - 1] as AccessLevel;
}

/** Que significa cada seccion y cada nivel, para la pantalla de perfiles. */
export interface SectionMeta {
  label: string;
  /** Una linea: que hay ahi. */
  summary: string;
  /** Que habilita cada nivel declarado de la seccion, `hidden` incluido. */
  levels: Partial<Record<AccessLevel, string>>;
  /** Si muestra datos personales de usuarios finales. */
  pii: boolean;
}

const HIDDEN_TEXT = 'Oculto: no aparece en el menú, ni por URL, ni en el manual.';

export const SECTION_META: Record<AccessSection, SectionMeta> = {
  dashboard: {
    label: 'Dashboard',
    summary: 'Estado general de cada plataforma: cuentas, suscripciones y actividad.',
    levels: { hidden: HIDDEN_TEXT, read: 'Ver los números.' },
    pii: false,
  },
  users: {
    label: 'Usuarios',
    summary: 'Buscar cuentas de usuarios finales y abrir su ficha completa.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Buscar y abrir la ficha (datos personales, suscripción, dispositivos).',
      write: 'Bloquear, cerrar sesiones, quitar dispositivos, dar pase de gracia, reconciliar pagos, investigar correos.',
    },
    pii: true,
  },
  partners: {
    label: 'Partners',
    summary: 'Las plataformas conectadas: configuración, planes, aplicaciones, términos.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Ver la lista y la configuración de cada plataforma.',
      write: 'Crear plataformas, editar su configuración, publicar planes y términos, rotar credenciales.',
    },
    pii: false,
  },
  catalog: {
    label: 'Catálogo',
    summary: 'Referencias que los usuarios marcan como favoritas.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Ver tipos e ítems.',
      write: 'Crear, editar y archivar tipos e ítems.',
    },
    pii: false,
  },
  reports: {
    label: 'Reportes',
    summary: 'Números contables del período.',
    levels: { hidden: HIDDEN_TEXT, read: 'Ver y exportar los reportes.' },
    pii: false,
  },
  invoices: {
    label: 'Facturas',
    summary: 'Comprobantes emitidos por cada cobro.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Buscar facturas y abrir el comprobante.',
      write: 'Reintentar una emisión fallida y reenviar por correo.',
    },
    pii: true,
  },
  jobs: {
    label: 'Jobs & DLQ',
    summary: 'Procesos automáticos y trabajo pendiente en las colas.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Ver el estado de los procesos y las colas.',
      write: 'Reprocesar la cola de descarte.',
    },
    pii: false,
  },
  audit: {
    label: 'Audit log',
    summary: 'Quién hizo qué y cuándo.',
    levels: { hidden: HIDDEN_TEXT, read: 'Consultar el registro.' },
    pii: false,
  },
  disposable: {
    label: 'Blacklist email',
    summary: 'Dominios de correo bloqueados al registrarse.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Ver la lista.',
      write: 'Agregar y quitar dominios.',
    },
    pii: false,
  },
  team: {
    label: 'Equipo',
    summary: 'Las cuentas del propio backoffice y sus perfiles de acceso.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Ver quién tiene cuenta y con qué perfil.',
      write: 'Crear cuentas, cambiar perfiles y permisos, restablecer contraseñas, editar perfiles de acceso.',
    },
    pii: false,
  },
  coupons: {
    label: 'Cupones',
    summary: 'Cupones de días gratis: emisión, estado y anulación.',
    levels: {
      hidden: HIDDEN_TEXT,
      read: 'Ver los cupones emitidos y su estado.',
      create: 'Emitir un cupón de días gratis.',
      revoke: 'Anular un cupón vigente.',
    },
    pii: false,
  },
};

function fill(level: AccessLevel, overrides: Partial<AccessMap> = {}): AccessMap {
  const out = {} as AccessMap;
  for (const s of ACCESS_SECTIONS) out[s] = overrides[s] ?? level;
  return out;
}

/** Igual que `fill`, pero con el nivel MAXIMO de cada seccion (no siempre `write`). */
function fillMax(overrides: Partial<AccessMap> = {}): AccessMap {
  const out = {} as AccessMap;
  for (const s of ACCESS_SECTIONS) out[s] = overrides[s] ?? maxLevelFor(s);
  return out;
}

/**
 * Los tres perfiles que trae el sistema. Son el estado INICIAL de la tabla
 * `admin_roles` (migracion 0039) y el fallback para sesiones emitidas antes
 * de que la sesion llevara el mapa. `admin` es inmutable y lo ve todo: usa el
 * nivel maximo de cada seccion, que no en todas es `write`.
 */
export const BUILTIN_ACCESS: Readonly<Record<'admin' | 'support' | 'readonly', AccessMap>> = {
  admin: fillMax(),
  // Soporte atiende usuarios: su ficha y sus facturas. Nada mas.
  support: fill('hidden', { users: 'write', invoices: 'read' }),
  // Mira el estado del sistema sin datos personales.
  readonly: fill('hidden', {
    dashboard: 'read',
    partners: 'read',
    catalog: 'read',
    jobs: 'read',
    audit: 'read',
  }),
};

export const ALL_HIDDEN: AccessMap = fill('hidden');

/**
 * Convierte un jsonb (o cualquier cosa) en un mapa completo. Lo que falte es
 * `hidden`; lo que no sea un nivel valido PARA ESA SECCION tambien. Una seccion
 * nueva agregada al catalogo queda oculta en todos los perfiles existentes
 * hasta que alguien la habilite — que es el default seguro.
 */
export function normalizeAccess(input: unknown): AccessMap {
  const src = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const out = {} as AccessMap;
  for (const s of ACCESS_SECTIONS) {
    const v = src[s];
    out[s] =
      typeof v === 'string' && (levelsFor(s) as readonly string[]).includes(v)
        ? (v as AccessLevel)
        : 'hidden';
  }
  return out;
}

/**
 * Orden global de capacidades, para quien no conoce la seccion. `atLeast` no
 * recibe seccion, asi que no puede usar la cadena de una en particular: cada
 * escalon tiene un rango total que respeta las cadenas existentes
 * (hidden < read < create < revoke y hidden < read < write). El chequeo por
 * seccion es `canLevel`.
 */
const RANK: Record<AccessLevel, number> = { hidden: 0, read: 1, create: 2, revoke: 3, write: 4 };

/** `level` alcanza para lo que pide `required`. */
export function atLeast(level: AccessLevel, required: AccessLevel): boolean {
  return RANK[level] >= RANK[required];
}

/**
 * El nivel que tiene el mapa en esa seccion esta en la CADENA de esa seccion en
 * una posicion igual o mas alta que `level`. Un nivel que la seccion no declara
 * (ej. `write` sobre `coupons`) no alcanza nunca.
 */
export function canLevel(map: AccessMap, section: AccessSection, level: AccessLevel): boolean {
  const chain = levelsFor(section);
  const have = chain.indexOf(map[section]);
  const need = chain.indexOf(level);
  if (have === -1 || need === -1) return false;
  return have >= need;
}

export function canRead(map: AccessMap, section: AccessSection): boolean {
  return canLevel(map, section, 'read');
}

/** Nivel mas alto de la seccion: `write` donde hay tres, `revoke` donde hay cuatro. */
export function canWrite(map: AccessMap, section: AccessSection): boolean {
  return canLevel(map, section, maxLevelFor(section));
}

// ---------------------------------------------------------------------------
// Manual (/docs)
// ---------------------------------------------------------------------------

/**
 * Seccion a la que pertenece una pagina del manual, por prefijo del slug
 * (sin idioma). `null` = pagina abierta a cualquier cuenta con sesion
 * (introduccion, primeros pasos, mi cuenta, referencia). `danger_zone` no es
 * una seccion del catalogo sino el permiso por cuenta que habilita zona
 * critica; el porton del borde lo sigue mirando.
 */
export type DocsGate = AccessSection | 'danger_zone';

const DOCS_PREFIXES = catalog.docs as readonly { prefix: string; section: DocsGate }[];

export function docsPageGate(slug: string): DocsGate | null {
  const clean = slug.replace(/^\/+/, '').replace(/\/+$/, '');
  for (const { prefix, section } of DOCS_PREFIXES) {
    if (prefix.endsWith('/')) {
      if (clean.startsWith(prefix)) return section;
    } else if (clean === prefix || clean.startsWith(`${prefix}/`)) {
      return section;
    }
  }
  return null;
}

/**
 * El pase de lectura del manual lleva el acceso codificado: un caracter por
 * seccion, en el orden del catalogo, mas una `d` final si la cuenta tiene
 * `danger_zone`. El caracter es el INDICE del nivel en la lista de esa seccion
 * (levelsFor), sobre un alfabeto de 32 simbolos; asi `0` es `hidden` en toda
 * seccion y cualquier otro caracter es un nivel visible. Es lo unico que la
 * CloudFront Function puede leer sin consultar a nadie, y entra comodo en una
 * cookie.
 */
const LEVEL_ALPHABET = '0123456789abcdefghijklmnopqrstuv';

export function encodeAccessForTicket(access: AccessMap, dangerZone: boolean): string {
  const letters = ACCESS_SECTIONS.map((s) => {
    const idx = levelsFor(s).indexOf(access[s]);
    return LEVEL_ALPHABET.charAt(idx < 0 ? 0 : idx);
  }).join('');
  return dangerZone ? `${letters}d` : letters;
}

// Formato viejo: una letra fija por seccion (h/r/w). Se sigue aceptando
// mientras haya pases en vuelo (techo 8 h) para que nadie rebote al login.
const LEGACY_TICKET = /^([hrw]+)(d?)$/;
const LEGACY_LEVEL: Record<string, AccessLevel> = { h: 'hidden', r: 'read', w: 'write' };
const MODERN_TICKET = /^[0-9a-v]+$/;

export function decodeAccessFromTicket(
  encoded: string,
): { access: AccessMap; dangerZone: boolean } | null {
  const legacy = LEGACY_TICKET.exec(encoded);
  if (legacy) {
    const letters = legacy[1] ?? '';
    const access = {} as AccessMap;
    ACCESS_SECTIONS.forEach((s, i) => {
      // Un pase mas corto que el catalogo (seccion agregada despues de emitido)
      // deja esa seccion oculta.
      access[s] = LEGACY_LEVEL[letters.charAt(i)] ?? 'hidden';
    });
    return { access, dangerZone: legacy[2] === 'd' };
  }

  // Formato nuevo. La `d` de danger_zone se separa por LONGITUD (una seccion
  // mas que el catalogo), no por el caracter: `d` tambien es un indice valido.
  let letters = encoded;
  let dangerZone = false;
  if (letters.length === ACCESS_SECTIONS.length + 1 && letters.endsWith('d')) {
    dangerZone = true;
    letters = letters.slice(0, -1);
  }
  if (letters.length === 0 || letters.length > ACCESS_SECTIONS.length) return null;
  if (!MODERN_TICKET.test(letters)) return null;

  const access = {} as AccessMap;
  for (let i = 0; i < ACCESS_SECTIONS.length; i++) {
    const section = ACCESS_SECTIONS[i] as AccessSection;
    const ch = letters.charAt(i);
    if (ch === '') {
      access[section] = 'hidden';
      continue;
    }
    const idx = LEVEL_ALPHABET.indexOf(ch);
    const chain = levelsFor(section);
    // Un indice fuera de la lista de esa seccion no es un nivel: pase invalido.
    if (idx < 0 || idx >= chain.length) return null;
    access[section] = chain[idx] as AccessLevel;
  }
  return { access, dangerZone };
}
